import React, { useRef } from 'react';
import { X, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import ColorPicker from './ColorPicker';
import { getText } from './constants';

export const AddNoteDialog = ({
  isOpen,
  onOpenChange,
  language,
  content,
  setContent,
  category,
  setCategory,
  color,
  setColor,
  imagePreview,
  onImageSelect,
  onImageRemove,
  onSave
}) => {
  const fileInputRef = useRef(null);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby="add-note-description">
        <DialogHeader>
          <DialogTitle>{getText('newNote', language)}</DialogTitle>
          <DialogDescription id="add-note-description">
            {getText('dialogDescription', language)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="note-content">{getText('content', language)}</Label>
            <Input
              id="note-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={getText('placeholder', language)}
              maxLength={200}
              data-testid="add-note-content"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="note-category">{getText('category', language)}</Label>
            <Input
              id="note-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder={getText('categoryPlaceholder', language)}
              maxLength={30}
              data-testid="add-note-category"
            />
          </div>
          
          <div className="space-y-2">
            <Label>{getText('color', language)}</Label>
            <ColorPicker value={color} onChange={setColor} language={language} />
          </div>
          
          {/* Image upload */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              {getText('addImage', language)}
              <span className="text-xs text-muted-foreground">({getText('imageHint', language)})</span>
            </Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onImageSelect}
              className="hidden"
              data-testid="add-note-image-input"
            />
            {imagePreview ? (
              <div className="relative">
                <img src={imagePreview} alt="Preview" className="w-full h-32 object-cover rounded-lg" />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 h-8 w-8"
                  onClick={onImageRemove}
                  data-testid="remove-image-btn"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-20 border-dashed"
                data-testid="add-image-btn"
              >
                <ImageIcon className="h-6 w-6 mr-2 text-muted-foreground" />
                {getText('addImage', language)}
              </Button>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-add-note">
              {getText('cancel', language)}
            </Button>
            <Button onClick={onSave} className="bg-[#006039] hover:bg-[#004d2e]" data-testid="save-add-note">
              {getText('save', language)}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddNoteDialog;
